import PageTemplate from "@/components/templates/PageTemplate";
import { useTrans } from "@/utils/useTrans";
import { Button, buttonVariants } from "@/v2-components/ui/button";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import CommunityReviewsSection from "@/v2-components/pages/about/_components/CommunityReviewsSection";
import ContactSection from "@/v2-components/pages/about/_components/ContactSection";
import GamePlatformGrid from "@/v2-components/pages/about/_components/GamePlatformGrid";
import HistorySection from "@/v2-components/pages/about/_components/HistorySection";
import PartnersSection from "@/v2-components/pages/about/_components/PartnersSection";
import ProductsSection from "@/v2-components/pages/about/_components/ProductsSection";
import SoftwareSection from "@/v2-components/pages/about/_components/SoftwareSection";
import { MEDIA_URLS } from "@/v2-components/pages/about/_constants/about-constants";
import Image from "next/image";
import React from "react";
import { Trans } from "next-i18next";
import { serverSideTranslations } from "@/libs/i18n/serverSideTranslations";
import Link from "next/link";
import { cn } from "@/libs/utils";

// 다른곳에서 쓰는 페이지 입니다.
// 기본적으로는 노출되지 않습니다.
export default function AboutPage() {
  const { t } = useTrans(["about"]);
  return (
    <PageTemplate>
      <PageTemplate.Content>
        <div className="mx-auto my-4 flex w-full max-w-screen-checkout-max flex-col items-start justify-start gap-16 overflow-hidden px-4 laptop:my-24 laptop:gap-24">
          {/* Hero Section */}
          <div className="relative flex flex-col items-start justify-start gap-7 self-stretch laptop:gap-12">
            <h1 className="justify-start self-stretch text-center text-2xl laptop:text-7xl">
              <Trans
                t={t}
                i18nKey="We connect people with <br/> digital worlds through <br/> consumer-ready haptic interfaces."
                components={{ br: <br /> }}
              />
            </h1>
            <div className="relative aspect-[328/240] w-full overflow-hidden rounded-xl laptop:aspect-[1196/640] laptop:rounded-2xl">
              <Image
                className="absolute hidden object-cover laptop:flex"
                src="/images/about/banner.png"
                alt="About bHaptics"
                fill
              />
              <Image
                className="absolute flex object-cover laptop:hidden"
                src="/images/about/m-banner.png"
                alt="About bHaptics"
                fill
              />
            </div>
          </div>

          {/* History Section */}
          <HistorySection />

          {/* Anniversary Video Section */}
          <div className="relative self-stretch overflow-hidden rounded-2xl">
            <video
              playsInline
              src={MEDIA_URLS.DESKTOP_VIDEO}
              poster={MEDIA_URLS.POSTER}
              className="object-cover"
              muted
              loop
              controls
              autoPlay
            />
          </div>

          {/* What's Haptics Section */}
          <div className="flex flex-col items-center justify-center gap-8 self-stretch">
            <h2 className="justify-start self-stretch text-center text-4xl">
              {t(`What's Haptics?`)}
            </h2>
            <div className="inline-flex flex-col-reverse items-center justify-start gap-6 self-stretch laptop:flex-row">
              <div className="inline-flex flex-1 flex-col items-center justify-center gap-8 laptop:items-start">
                <div className="justify-start self-stretch text-lg">
                  <p>
                    {t(
                      `Haptics uses technology to simulate the experience of touch in digital experiences, making experiences more immersive and intuitive. At bHaptics, we use this technology in our devices to enhance your digital experiences.`,
                    )}
                  </p>
                  <p className="my-4">
                    {t(
                      `From Gaming, to Movies, to listening to music. We create comfortable, wearable and highly connected devices that level up your entertainment experiences.`,
                    )}
                  </p>
                  <p>
                    {t(
                      `Our ultimate goal: to provide more natural and immersive experience for all kind of conventional digital content such as games, videos, music, and movies not only with audio and visual but also with haptics.`,
                    )}
                  </p>
                </div>
                {/* ★404 였다 (전수감사 2026-09-09). 종전 링크는 Notion UUID 슬러그이고,
                      블로그가 git 관리 마크다운으로 옮겨져 슬러그가 사람이 읽는 값이 됐다
                      (`content/blog/what-is-haptics/`). prod 실측: 옛 링크 **404** · 새 링크
                      **200**. `/en/about/` HTML 에 그대로 렌더되던 자리다.
                  
                      쿼리(`title`·`subtitle`·`tag`·`createTime`)도 걷는다 — Notion 시절 목록에서
                      넘기던 값이고 새 상세는 마크다운 front-matter 를 읽는다. */}
                <Link
                  href={"/blog/what-is-haptics/"}
                  className={cn(
                    buttonVariants({
                      variant: "blue-text",
                      size: "sm",
                      className: "gap-2 px-0",
                    }),
                  )}
                >
                  <div className="justify-start text-sm font-600 tracking-tight text-blue-600">
                    {t(`Learn More`)}
                  </div>
                  <ChevronRightIcon className="h-4 w-4" />
                </Link>
              </div>
              <video
                playsInline
                src={"/media/about/what-is-bhaptics.mp4"}
                muted
                controls
                loop
                autoPlay
                className="relative aspect-[586/400] w-full overflow-hidden rounded-2xl laptop:w-[586px]"
              />
            </div>
          </div>

          <ProductsSection />
          <GamePlatformGrid />
          <SoftwareSection />
          <PartnersSection />
          <CommunityReviewsSection />
          <ContactSection />
        </div>
      </PageTemplate.Content>
    </PageTemplate>
  );
}

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["gnb", "about"])),
    },
  };
}
